<template>
  <v-app id="schedule" :class="{ iframe: isFrame }">
    <v-container class="page">
      <v-stepper v-if="ready" v-model="scheduleStep" class="schedule-stepper">
        <v-stepper-header v-show="!saved">
          <div :key="idx" v-for="(step, idx) in steps">
            <v-tooltip top>
              <v-stepper-step
                slot="activator"
                :editable="idx < scheduleStep"
                :complete="idx < scheduleStep"
                :step="idx + 1"
              >
                {{ step.name }}
              </v-stepper-step>
              <span>{{ step.name }}</span>
            </v-tooltip>
          </div>
        </v-stepper-header>

        <v-stepper-items>
          <template v-for="(step, idx) in steps">
            <v-stepper-content :key="idx" :step="idx + 1">
              <component
                :is="step.component"
                :inspection="inspection"
                :active="scheduleStep === idx + 1"
                :params="stepParams(step.component)"
                :field-errors="fieldErrorsForStep(step.component)"
                @change="stepChange"
              />
              <v-layout
                v-if="step.component === 'ContactInfo' && selectScheduler"
                wrap
                justify-center
                class="scheduler-select"
              >
                <v-flex xs11 md10 class="scheduler-select-inner">
                  <v-layout wrap justify-center>
                    <v-flex xs12 center section class="scheduler-select-header">
                      Who are you?
                    </v-flex>
                    <v-layout wrap justify-center class="scheduler-select-body">
                      <v-flex
                        xs12
                        md4
                        class="scheduler-block"
                        @click="setScheduler('client')"
                      >
                        <div class="scheduler-icon">
                          <i class="fa fa-home" />
                        </div>
                        <div class="scheduler-name light">The Client</div>
                      </v-flex>
                      <v-flex
                        xs12
                        md4
                        class="scheduler-block"
                        @click="setScheduler('agent')"
                      >
                        <div class="scheduler-icon">
                          <i class="fa fa-user" />
                        </div>
                        <div class="scheduler-name light">The Agent</div>
                      </v-flex>
                      <v-flex
                        xs12
                        md4
                        class="scheduler-block"
                        @click="setScheduler(null)"
                      >
                        <div class="scheduler-icon">
                          <i class="fa fa-search" />
                        </div>
                        <div class="scheduler-name light">Someone Else</div>
                      </v-flex>
                    </v-layout>
                  </v-layout>
                </v-flex>
              </v-layout>
              <v-layout
                v-if="step.component === 'ContactInfo' && showConsentPrompt"
                wrap
                justify-center
                class="scheduler-select"
                data-testid="consent-prompt"
              >
                <v-flex xs11 md10 class="scheduler-select-inner">
                  <v-layout wrap justify-center>
                    <v-flex xs12 center section class="scheduler-select-header">
                      Text message updates
                    </v-flex>
                    <v-layout wrap justify-center class="scheduler-select-body">
                      <v-flex
                        xs12
                        class="consent-prompt-message"
                        data-testid="consent-prompt-message"
                      >
                        You haven't opted in to transactional text messages, so
                        you won't receive texts from your inspector about your
                        inspection or your inspection report.
                      </v-flex>
                      <v-flex xs12 center class="consent-prompt-actions">
                        <div
                          class="btn btn-large consent-prompt-btn"
                          data-testid="consent-prompt-opt-in"
                          @click="optInToTransactionalConsent"
                        >
                          Opt In
                        </div>
                        <div
                          class="btn btn-large btn-flat consent-prompt-btn"
                          data-testid="consent-prompt-continue-anyway"
                          @click="continueWithoutTransactionalConsent"
                        >
                          Continue Anyway
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-layout>
                </v-flex>
              </v-layout>
              <div v-if="disabled" class="ineligible-wrap">
                <div class="ineligible-message">
                  This company cannot currently accept inspection requests
                  online.<br />Please call to schedule.
                </div>
              </div>
            </v-stepper-content>
          </template>
        </v-stepper-items>
      </v-stepper>
      <div v-if="!saved" class="button-row">
        <div
          v-if="!disabled"
          @click="next"
          class="btn btn-xlarge"
          :class="stepClass"
        >
          <div v-if="!lastStep" class="inner" id="schedule-next-btn">
            Next
            <i class="fa fa-chevron-right" />
          </div>
          <div
            v-else-if="lastStep && !saving"
            class="inner"
            id="schedule-schedule-btn"
          >
            <i class="fa fa-check" />
            Schedule
          </div>
          <div v-else class="inner" id="schedule-saving-btn">
            <i class="fa fa-spinner fa-spin" />
            Saving
          </div>
        </div>
      </div>

      <toast />
    </v-container>
  </v-app>
</template>

<script>
import { mapState } from 'vuex'
import axios from '../../AxiosService'
import dig from '../../Dig'
import moment from 'moment'
import Toast from '../notifier/Toast'
import schedulerWeeksShown from '@/utils/schedulerWeeksShown'

// Steps
import ContactInfo from './scheduler/ContactInfo'
import ReviewInspection from './scheduler/ReviewInspection'
import SelectDate from './scheduler/SelectDate'
import SelectService from './scheduler/SelectService'

const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

// Every person the ContactInfo step can collect, in form order.
const CONSENT_PEOPLE = ['buyer', 'buyer_2', 'agent', 'selling_agent']

// SHUA-4319 — inline, at-the-field validation errors.
//
// `validateStep()` already produces field-grained string codes; these maps turn them into
// per-field messages without re-deriving any validation. The toast keeps its own copy of the
// copy via `displayErrors()`, which is deliberately left untouched.
//
// `field` is the key the shared components look themselves up by (see mixins/fieldErrors.js).
// Several codes collapse onto one field — every `year_built*` variant marks the same control —
// and the *first* matching code for a field wins, so the more specific message is listed first.
const FIELD_ERROR_CODES = {
  SelectService: [
    ['service', 'service', 'Please select a Service'],
    ['property', 'property', 'Please enter a property address'],
    ['address', 'address', 'Please enter the street address'],
    ['zip', 'zip', 'Please enter the zip code'],
    ['square_feet_numericality', 'square_feet', 'Please enter the square footage as a whole number'],
    ['square_feet_negative', 'square_feet', 'Please enter a square footage greater than 0'],
    ['square_feet', 'square_feet', 'Please enter the square footage'],
    ['year_built_numericality', 'year_built', 'Please enter the year built as a whole number'],
    ['year_built_negative', 'year_built', 'Please enter a year built greater than 0'],
    ['year_built_in_the_future', 'year_built', "Please enter a year built that isn't in the future"],
    ['year_built', 'year_built', 'Please enter the year built'],
  ],
  ContactInfo: [
    ['client_first_name', 'client_first_name', "Please enter the client's first name"],
    ['client_last_name', 'client_last_name', "Please enter the client's last name"],
    ['client_email_invalid', 'client_email', 'Please enter a valid email address'],
    ['client_email', 'client_email', "Please enter the client's email address"],
    ['client_phone', 'client_phone', 'Please enter a valid phone number'],
    ['buyer_sms_opt_in', 'buyer_sms_opt_in', 'Please check the SMS agreement to continue'],
  ],
  SelectDate: [
    ['datetime', 'datetime', 'Please select an available time'],
    ['inspector', 'inspector', 'Please select an inspector'],
  ],
}

// The Company/Organization control has one message per channel. They deliberately differ:
// PM QA (SHUA-4319) re-voiced the inline validations to the "Please …" form while leaving the
// toast copy alone, and this field is the only one whose message feeds both.
const COMPANY_NAME_INLINE_MESSAGE = 'Please enter the Company/Organization name'
const COMPANY_NAME_TOAST_MESSAGE = 'Add the Company/Organization name.'

// Field key -> candidate selectors for the scroll/focus target, in preference order. The first
// one present in the DOM wins, which is what lets one key serve both PropertyForm modes: in
// Google-Places search mode the manual inputs don't exist and the search box stands in.
const FIELD_ERROR_TARGETS = {
  service: ['#service-selection-dropdown'],
  property: ['#property-address-search', '#address-address'],
  address: ['#address-address', '#property-address-search'],
  zip: ['#address-zip', '#property-address-search'],
  square_feet: ['#address-square-feet'],
  year_built: ['#address-year-built'],
  client_first_name: ['#client-first-name__1', '#client-company-name__1'],
  client_last_name: ['#client-last-name__1'],
  client_email: ['#client-email__1'],
  client_phone: ['#client-phone__1'],
  buyer_sms_opt_in: ['#client-sms-opt-in__1'],
  datetime: ['#schedule-calendar', '#schedule-select-date'],
  inspector: ['#inspector-selection-dropdown', '#inspector-selection-error'],
}

// DOM order per step, so "the first invalid control" is well defined rather than
// dependent on the order codes happen to be pushed in. Custom fields come last on
// SelectService and are appended dynamically as `custom_<index>`.
const FIELD_ERROR_ORDER = {
  SelectService: [
    'service',
    'property',
    'address',
    'zip',
    'square_feet',
    'year_built',
  ],
  ContactInfo: [
    'client_first_name',
    'client_last_name',
    'client_email',
    'client_phone',
    'buyer_sms_opt_in',
  ],
  SelectDate: ['datetime', 'inspector'],
}

// 56px MaterializeCSS fixed nav + breathing room. Mirrors the `scroll-margin-top` in this
// component's stylesheet; kept here too because `scroll-margin-top` is not honoured by every
// engine this scheduler has to work on.
const SCROLL_MARGIN_TOP = 72
const SCROLL_MARGIN_TOP_IFRAME = 16

export default {
  components: {
    ContactInfo,
    ReviewInspection,
    SelectDate,
    SelectService,
    Toast,
  },
  computed: {
    ...mapState('inspection', [
      'charges',
      'company',
      'customFields',
      'userProfile',
    ]),
    // Company mode swaps First/Last Name for a single Company/Organization control that binds
    // the same `first_name`. Both the inline message and the toast branch on this.
    buyerShowAsCompany() {
      return dig(this, 'inspection.buyer.attributes.show_as_company', false)
    },
    contactFirst() {
      return this.company.attributes.settings.scheduler_contacts_first
    },
    currentComponent() {
      return this.currentStep.component
    },
    currentStep() {
      return this.steps[this.scheduleStep - 1]
    },
    days() {
      let m = moment()
      let days = Array.from(
        { length: this.weeksShown * 7 + 1 },
        (_, i) => i
      )
      return days.map((d) => {
        if (d > 0) m.add(1, 'days')
        return m.clone()
      })
    },
    defaultSteps() {
      let steps = [{ name: 'What & Where', component: 'SelectService' }]

      let middle = [
        { name: 'Who to contact', component: 'ContactInfo' },
        { name: 'When', component: 'SelectDate' },
      ]

      if (!this.contactFirst) middle = middle.reverse()
      steps = steps.concat(middle)

      steps.push({ name: 'Review & Schedule', component: 'ReviewInspection' })
      return steps
    },
    gaEnabled() {
      return (
        dig(this, 'company.attributes.settings.scheduler_completion_page', '')
          .length > 0 &&
        dig(this, 'company.attributes.settings.ga_property', '').length > 0
      )
    },
    isFrame() {
      return !!this.$route.query.iframe
    },
    smsConsentEnabled() {
      return !dig(this, 'company.attributes.settings.disable_text_messages_consent', false)
    },
    // Also gated on `smsConsentEnabled`: with the consent UI off there is no checkbox to decline.
    promptTransactionalConsentEnabled() {
      return (
        this.smsConsentEnabled &&
        !!dig(
          this,
          'company.attributes.settings.scheduler_prompt_transactional_consent',
          false
        )
      )
    },
    lastStep() {
      return this.scheduleStep === this.steps.length
    },
    preselectAgent() {
      return (
        this.userProfile.id &&
        this.userProfile.id === this.$route.query.buying_agent_id
      )
    },
    schedulingDisabled() {
      return dig(this, 'company.attributes.scheduling_disabled', false)
    },
    stepClass() {
      return this.lastStep ? 'btn-info' : ''
    },
    title() {
      return `${this.$route.params.companySlug} | Book an inspection`
    },
    weeksShown() {
      return schedulerWeeksShown(this.company)
    },
  },
  created() {
    this.queryParams = Object.assign({}, this.$route.query)
    this.fetchCompany()
  },
  data() {
    return {
      disabled: false,
      // SHUA-4319 — `{ fieldKey: message }` for the current step only. Empty until a NEXT tap
      // fails validation, and reset on every step change.
      fieldErrors: {},
      // Every key the wizard will ever attach to `inspection` is declared HERE, seeded `null`.
      //
      // WHY: Vue 2 cannot intercept a key ADDED to an already-observed object, so a key that first
      // appears via a plain assignment is invisible to the reactivity system. The deep `inspection`
      // watcher below never subscribes beneath such a key, and `refreshFieldErrors()` — which
      // clears a field's inline error as soon as the client corrects it — therefore never runs for
      // it: the client fixed their email and the stale message stayed on screen until the next
      // NEXT tap.
      //
      // The keys are attached later by `scheduler/ContactInfo.vue` (`agent`, `buyer`, `buyer_2`,
      // `selling_agent`), `scheduler/SelectService.vue` (`add_ons`, `property`),
      // `scheduler/SelectDate.vue` (`inspector`), and this component (`custom_fields_attributes`,
      // `referral_source`). Declaring them up front makes those assignments reactive writes and
      // needs no `$set` at any call site.
      //
      // `null` rather than `{}`/`[]`: it is indistinguishable from an absent key at every read site
      // — `Dig.js` returns its fallback for `null` exactly as for a missing key, and the truthiness
      // guards (`if (!property)`, `!dig(this, 'inspection.inspector')`) keep answering the same way.
      // A `{}` or `[]` seed would silently flip those guards.
      //
      // ANY new `inspection.<key>` must be declared here too, or it will be non-reactive again.
      inspection: {
        attributes: { settings: {} },
        services: [],
        add_ons: null,
        agent: null,
        buyer: null,
        buyer_2: null,
        custom_fields_attributes: null,
        inspector: null,
        property: null,
        referral_source: null,
        selling_agent: null,
      },
      openings: [],
      partialRegistered: false,
      queryParams: {},
      ready: false,
      saved: false,
      saving: false,
      selectScheduler: false,
      schedulerSelected: false,
      // `acknowledged` latches, so the prompt is never re-asked on the way back through.
      showConsentPrompt: false,
      consentPromptAcknowledged: false,
      scheduleStep: 1,
      steps: [],
    }
  },
  metaInfo() {
    return {
      title: this.title,
    }
  },
  watch: {
    // A corrected field clears its own error without needing another NEXT tap. Deep, because
    // every value lives under `inspection.*.attributes.*`.
    inspection: {
      deep: true,
      handler() {
        this.refreshFieldErrors()
      },
    },
    // Errors belong to the step that produced them.
    scheduleStep() {
      this.fieldErrors = {}
    },
  },
  methods: {
    askForScheduler() {
      return (
        !this.schedulerSelected &&
        dig(this, 'inspection.buyer.attributes.email', '').length > 0 &&
        dig(this, 'inspection.agent.attributes.email', '').length > 0
      )
    },
    // Only `sms_opt_in` is read, so a marketing-only decline never raises the prompt.
    //
    // A method, not a computed, for the same reason `askForScheduler` is one: ContactInfo attaches
    // people with `this.inspection.buyer = client`, which Vue 2 does not make reactive — a computed
    // would evaluate once against an empty inspection and never invalidate. Presence is keyed on an
    // entered email/phone because this component cannot see ContactInfo's local
    // `hasClient2` / `hasListingAgent` toggles.
    peopleDecliningTransactionalConsent() {
      return CONSENT_PEOPLE.map((person) =>
        dig(this, `inspection.${person}.attributes`)
      ).filter(
        (attributes) =>
          attributes &&
          (dig(attributes, 'email', '').length > 0 ||
            dig(attributes, 'phone', '').length > 0) &&
          !attributes.sms_opt_in
      )
    },
    // Consulted only after `validateStep()` passes, so the prompt never masks a field error.
    shouldPromptForTransactionalConsent() {
      return (
        this.promptTransactionalConsentEnabled &&
        !this.consentPromptAcknowledged &&
        this.peopleDecliningTransactionalConsent().length > 0
      )
    },
    // `$set` is required, not defensive: `attributes` starts empty, so a plain assignment would
    // record consent without re-rendering SmsConsentFields' `:input-value` checkbox. Assigning the
    // bound model is also what keeps it animation-free.
    optInToTransactionalConsent() {
      // Single-activation guard. `showConsentPrompt` is cleared synchronously below, so a second
      // activation returns here rather than running the advance path twice — which on a
      // contacts-last company (the contact step sits immediately before Review) would hit
      // `lastStep` and `save()` the booking without ever rendering the review step.
      if (!this.showConsentPrompt) return

      this.peopleDecliningTransactionalConsent().forEach((attributes) => {
        this.$set(attributes, 'sms_opt_in', true)
      })
      this.consentPromptAcknowledged = true
      this.showConsentPrompt = false
      this.next()
    },
    // A nudge, never a gate — resumes the untouched advance path with consent left as it was.
    continueWithoutTransactionalConsent() {
      // Same single-activation guard as the opt-in path above.
      if (!this.showConsentPrompt) return

      this.consentPromptAcknowledged = true
      this.showConsentPrompt = false
      this.next()
    },
    calculateCharges() {
      return this.$store.dispatch(
        'inspection/calculateCharges',
        this.inspection
      )
    },
    displayErrors(errors) {
      if (errors.length > 0) {
        this.submitErrorsToGA()

        let comp = this.currentStep.component
        let errorMsg = ''

        if (comp === 'SelectService') {
          if (errors.includes('service') || errors.includes('property')) {
            if (errors.includes('service'))
              errorMsg += 'You need to select a Service first!<br/>'
            if (errors.includes('property'))
              errorMsg += 'You forgot to add a property to inspect!<br/>'
          } else {
            let propertyMsg =
              'Your property is missing the following info:<br/>'
            if (errors.includes('address')) propertyMsg += '- an address<br/>'
            if (errors.includes('zip')) propertyMsg += '- a zip code<br/>'
            // Square footage errors
            const squareFootageErrors = '- Its square footage'
            if (errors.includes('square_feet'))
              propertyMsg += `${squareFootageErrors}<br/>`
            if (errors.includes('square_feet_numericality'))
              propertyMsg += `${squareFootageErrors} must be an integer<br/>`
            if (errors.includes('square_feet_negative'))
              propertyMsg += `${squareFootageErrors} must be greater than 0<br/>`
            // Year built errors
            const yearBuiltErrors = '- The year it was built'
            if (errors.includes('year_built'))
              propertyMsg += `${yearBuiltErrors}<br/>`
            if (errors.includes('year_built_numericality'))
              propertyMsg += `${yearBuiltErrors} must be an integer<br/>`
            if (errors.includes('year_built_negative'))
              propertyMsg += `${yearBuiltErrors} must be greater than 0<br/>`
            if (errors.includes('year_built_in_the_future'))
              propertyMsg += `${yearBuiltErrors} can't be in the future<br/>`
            errorMsg += propertyMsg
          }
          if (errors.includes('custom'))
            errorMsg += "You're missing some required additional info!"
        } else if (comp === 'ContactInfo') {
          if (errors.includes('client_email'))
            errorMsg +=
              'You forgot the Client Email! Add it so we can contact you!<br/>'
          if (errors.includes('client_first_name'))
            errorMsg += this.buyerShowAsCompany
              ? `${COMPANY_NAME_TOAST_MESSAGE}<br/>`
              : 'You forgot to add the Client First Name.<br/>'
          if (errors.includes('client_last_name'))
            errorMsg += 'You forgot to add the Client Last Name.<br/>'
          if (errors.includes('client_phone'))
            errorMsg += "It doesn't look like your Client Phone is valid.<br/>"
          if (errors.includes('client_email_invalid'))
            errorMsg += "It doesn't look like your Client email is valid.<br/>"
          if (errors.includes('client_2_email_invalid'))
            errorMsg +=
              "It doesn't look like your Client #2 email is valid.<br/>"
          if (errors.includes('agent_email_invalid'))
            errorMsg += "It doesn't look like your Agent email is valid.<br/>"
          if (errors.includes('selling_agent_email_invalid'))
            errorMsg +=
              "It doesn't look like your Listing Agent email is valid."
        } else if (comp === 'SelectDate') {
          if (errors.includes('datetime'))
            errorMsg +=
              "Select a time you'd like to have your inspection at!<br/>"
          if (errors.includes('inspector'))
            errorMsg += 'Select an inspector for your inspection!'
        }

        this.$store.dispatch('notifier/show', { type: 'Error', msg: errorMsg })
      }
    },
    duration() {
      return this.charges.reduce((summ, c) => {
        summ += c.duration
        return summ
      }, 0)
    },
    fetchCompany() {
      this.$store
        .dispatch('inspection/fetchCompany', {
          id: this.$route.params.companySlug,
        })
        .then(() => {
          this.steps = this.defaultSteps
          this.fetchCustomFields().then(() => {
            this.$store
              .dispatch('inspection/fetchUserProfile')
              .then(() => {
                this.initializeScheduling()
              })
              .catch(() => {
                this.initializeScheduling()
              })
          })
        })
    },
    fetchCustomFields() {
      return this.$store.dispatch('inspection/fetchCustomFields').then(() => {
        this.inspection.custom_fields_attributes = this.customFields.map(
          (cf) => {
            return {
              attributes: {
                name: cf.attributes.name,
                required: cf.attributes.required,
                hidden: cf.attributes.hidden,
                mobile: cf.attributes.mobile,
                choices: cf.attributes.choices,
                custom_field_template_id: cf.id,
                response_format: cf.attributes.response_format,
                priority: cf.attributes.priority,
                value: cf.attributes.default_value,
              },
            }
          }
        )
      })
    },
    fetchOpenings() {
      let requests = this.openingUrls().map((url) => {
        return axios.get(url)
      })

      return Promise.all(requests).then((response) => {
        this.openings = this.days.map((d, i) => {
          return {
            date: d.toDate(),
            times: response[i].data,
          }
        })
      })
    },
    initializeInspection() {
      this.inspection.attributes.company_id = this.company.id
      this.inspection.attributes.requested_on = new Date()
      this.inspection.attributes.paid_required = this.company.attributes.settings.client_portal_collect_payment_after_publish || this.company.attributes.settings.paid_required_default
      this.inspection.attributes.settings.scheduled_at = new Date()
      this.inspection.attributes.settings.scheduled_by = 'Online Scheduler'
      this.inspection.attributes.settings.enable_auto_tracks =
        this.company.attributes.settings.enable_auto_tracks
      this.inspection.attributes.settings.occupied = false
      this.inspection.attributes.settings.utilities_on = false

      if (this.$route.query.referral_source) {
        this.inspection.referral_source = this.$route.query.referral_source
      }

      if (parseInt(this.company.attributes.settings.last_order_id)) {
        this.inspection.attributes.order_id =
          parseInt(this.company.attributes.settings.last_order_id) + 1
      }

      if (this.company.attributes.settings.scheduler_require_confirmation) {
        this.inspection.attributes.confirmed = false
        this.inspection.attributes.settings.disable_notifications = true
      } else {
        this.inspection.attributes.confirmed = true
      }

      let isCCR =
        dig(this, 'userProfile.type', '') === 'call_center_rep' ||
        this.$route.params.ccr_view
      if (isCCR) {
        this.inspection.attributes.settings.scheduled_by = `CCR: ${this.userProfile.attributes.name}`
        this.inspection.attributes.call_center_id = dig(
          this,
          'company.attributes.call_center_id'
        )
      }

      UTM_FIELDS.map((field) => {
        const param = this.queryParams[field]
        if (param) this.inspection.attributes[field] = param
      })
    },
    initializeScheduling() {
      if (this.schedulingDisabled) {
        this.disabled = true
        this.ready = true
      } else {
        this.initializeInspection()
        this.ready = true
      }
    },
    next() {
      if (!this.lastStep) {
        let valid = this.validateStep()
        let proceed = false

        if (!valid) {
          // After the messages have rendered, so the control has its final position.
          this.$nextTick(() => this.focusFirstInvalidField())
        }

        if (valid) {
          if (this.currentComponent === 'SelectService') {
            this.calculateCharges().then(() => {
              this.fetchOpenings()
            })
            proceed = true
          } else if (this.currentComponent === 'ContactInfo') {
            if (this.askForScheduler()) {
              this.selectScheduler = true
            } else if (this.shouldPromptForTransactionalConsent()) {
              this.showConsentPrompt = true
            } else {
              proceed = true
              this.registerPartial()
            }
          } else {
            this.calculateCharges().then(() => {})
            proceed = true
          }
          if (proceed) {
            this.scheduleStep += 1
            let offset = document.querySelector('#page-content').offsetHeight
            window.scrollTo({ left: 0, top: offset, behavior: 'smooth' })
          }
        }
      } else {
        this.save()
      }
    },
    openingUrls() {
      let lat = dig(this.inspection, 'property.attributes.lat', '')
      let lng = dig(this.inspection, 'property.attributes.lng', '')
      let duration = this.duration()
      let serviceIds = dig(this.inspection, 'services', []).map((s) => {
        return s.id
      })
      let addOnIds = dig(this.inspection, 'add_ons', []).map((ao) => {
        return ao.id
      })

      serviceIds =
        serviceIds.length > 0 ? serviceIds.join('&service_id[]=') : null
      addOnIds =
        addOnIds.length > 0 ? addOnIds.join('&service_add_on_id[]=') : null

      return this.days.map((d) => {
        let date = d.format('YYYY-M-D')
        let url = `/time_slots/open/${this.company.id}/${date}?duration=${duration}&lng=${lng}&lat=${lat}`
        if (serviceIds) url += `&service_id[]=${serviceIds}`
        if (addOnIds) url += `&service_add_on_id[]=${addOnIds}`
        return url
      })
    },
    registerPartial() {
      if (
        (this.company.attributes.settings.scheduler_receive_partial_email ||
          this.company.attributes.settings.scheduler_receive_partial_text) &&
        !this.partialRegistered
      ) {
        let data = `Schedule by ${this.inspection.attributes.settings.scheduled_by} In Progress\n\nServices: `
        data += this.inspection.services
          .map((s) => {
            return s.attributes.name
          })
          .join(' ')
        data += '\n'

        let buyer = this.inspection.buyer.attributes
        if (dig(buyer, 'first_name', '').length > 0)
          data += `Client First Name: ${buyer.first_name}\n`
        if (dig(buyer, 'last_name', '').length > 0)
          data += `Client Last Name: ${buyer.last_name}\n`
        if (dig(buyer, 'phone', '').length > 0)
          data += `Client Phone: ${buyer.phone}\n`
        if (buyer.email.length > 0) data += `Client Email: ${buyer.email}\n`
        data += '\n'

        let agent = dig(this, 'inspection.agent.attributes')
        if (agent) {
          if (dig(agent, 'first_name', '').length > 0)
            data += `Agent First Name: ${agent.first_name}\n`
          if (dig(agent, 'last_name', '').length > 0)
            data += `Agent Last Name: ${agent.last_name}\n`
          if (dig(agent, 'phone', '').length > 0)
            data += `Agent Phone: ${agent.phone}\n`
          if (dig(agent, 'email', '').length > 0)
            data += `Agent Email: ${agent.email}\n`
          data += '\n'
        }

        let property = this.inspection.property.attributes
        data += `${property.address} ${property.city} ${property.state} ${property.zip}\n`
        data += `${property.square_feet} sq ft\n`
        data += `Built ${property.year_built}`

        let address = `${property.address}, ${property.city} ${property.state} ${property.zip}`

        axios
          .post('/schedule_in_progress', {
            company_id: this.company.id,
            info: data,
            address: address,
          })
          .then(() => {
            this.partialRegistered = true
          })
      }
    },
    save() {
      if (!this.saving) {
        this.saving = true

        let inspectionObject = this.saveInspectionObject()

        this.checkForOverlaps().then((conflicts) => {
          if (conflicts) {
            this.fetchOpenings().then(() => {
              alert(
                "We're sorry, but this time is no longer available. Please select a different time for your inspection."
              )
              this.scheduleStep =
                this.steps.findIndex((s) => s.name === 'When') + 1
              this.saving = false
            })
          } else {
            this.$store
              .dispatch('inspection/createInspection', inspectionObject)
              .then((response) => {
                this.saved = true
                this.submitToGA()
              })
              .catch((e) => {
                console.log(e)
                this.saving = false
              })
          }
        }).catch((e) => {
          alert(
            "Something went wrong. Please try again."
          )
          console.log(e)
          this.saving = false
        })
      }
    },
    saveInspectionObject() {
      let object = {
        inspection: JSON.parse(JSON.stringify(this.inspection)).attributes,
      }
      object.inspection.property_attributes = dig(
        this,
        'inspection.property.attributes'
      )

      Object.keys(object.inspection.settings).forEach((k) => {
        object.inspection[k] = object.inspection.settings[k]
      })

      object.inspection.duration = this.duration()
      object.inspection.buyer_attributes = dig(
        this.inspection,
        'buyer.attributes'
      )

      // Consent keys are only sent when the checkboxes were shown, so a company that
      // disabled the consent UI never records an implicit not_opted_in.
      if (this.smsConsentEnabled) {
        object.inspection.buyer_attributes.sms_opt_in = !!object.inspection.buyer_attributes.sms_opt_in
        object.inspection.buyer_attributes.sms_marketing_opt_in = !!object.inspection
          .buyer_attributes.sms_marketing_opt_in
      } else {
        delete object.inspection.buyer_attributes.sms_opt_in
        delete object.inspection.buyer_attributes.sms_marketing_opt_in
      }

      object.inspection.buyer_2_attributes = dig(
        this.inspection,
        'buyer_2.attributes'
      )
      if (this.preselectAgent) {
        object.inspection.buying_agent_id = this.$route.query.buying_agent_id
      } else {
        object.inspection.agent_attributes = dig(
          this.inspection,
          'agent.attributes'
        )
      }
      object.inspection.selling_agent_attributes = dig(
        this.inspection,
        'selling_agent.attributes'
      )
      object.inspection.inspector_id = this.inspection.inspector.id

      object.inspection.services = (this.inspection.services || []).map((s) => {
        return s.id
      })
      object.inspection.add_ons = (this.inspection.add_ons || []).map((ao) => {
        return ao.id
      })
      object.inspection.internachi_buyback = !!(
        this.inspection.services || []
      ).find((s) => s.attributes.internachi_buyback)

      object.inspection.custom_fields_attributes =
        this.inspection.custom_fields_attributes.map((cf) => {
          return cf.attributes
        })

      if (dig(this, 'inspection.inspectorRequested')) {
        object.inspector_requests = [this.inspection.inspector.id]
      }

      return object
    },
    checkForOverlaps() {
      let dt = moment(
        this.inspection.attributes.datetime,
        'ddd MMM DD YYYY h:m A'
      )
      const datetimeFormatted = dt.format('YYYY-MM-DD hh:mma')
      let params = {
        duration: this.duration(),
        inspection_placeholder_id:
          this.inspection.attributes.inspection_placeholder_id,
        inspector_ids: [this.inspection.inspector.id],
        online_scheduler: true,
      }

      return axios
        .get(`/can_schedule/${this.company.id}/${datetimeFormatted}`, {
          params: params,
        })
        .then((response) => {
          let confs = response.data.conflicts || ''
          confs = confs.split('.').filter((c) => c.match(/inspection|event/))
          if (confs.length > 0) {
            return confs
          }
        })
    },
    setScheduler(scheduler) {
      let name = null

      if (scheduler === 'client') {
        name = 'Client'
      } else if (scheduler === 'agent') {
        let first = dig(this, 'inspection.agent.attributes.first_name', '')
        let last = dig(this, 'inspection.agent.attributes.last_name', '')

        if (first.length > 0 && last.length > 0) {
          name = `${first} ${last}`
        } else {
          name = dig(this, 'inspection.agent.attributes.email', '')
        }

        name = `Agent ${name}`
      }

      if (name)
        this.inspection.attributes.settings.scheduled_by = `${name} via the Online Scheduler`

      this.selectScheduler = false
      this.schedulerSelected = true
      this.next()
    },
    stepChange() {
      if (this.currentComponent === 'SelectDate') {
        this.next()
      }
    },
    stepParams(component) {
      if (component === 'SelectDate') {
        return {
          openings: this.openings,
        }
      } else if (component === 'ReviewInspection') {
        return {
          saved: this.saved,
        }
      } else {
        return {}
      }
    },
    submitErrorsToGA() {
      if (typeof ga === 'function') {
        // eslint-disable-next-line no-undef
        ga(
          'send',
          'event',
          'trigger',
          'alert',
          `step_${this.scheduleStep}_missing_info`
        )
      }
    },
    submitToGA() {
      if (typeof ga === 'function' && this.gaEnabled) {
        // eslint-disable-next-line no-undef
        ga('create', this.company.attributes.settings.ga_property, {
          cookieFlags: 'max-age=7200;secure;samesite=none',
        })
        // eslint-disable-next-line no-undef
        ga(
          'send',
          'pageview',
          this.company.attributes.settings.scheduler_completion_page
        ) // eslint-disable-line
      }
    },
    // Pure: returns the error codes for the current step and the indexes of the custom fields
    // that are missing a required answer. No toast, no state mutation — so it can be re-run
    // freely (e.g. by `refreshFieldErrors`) without the user seeing anything.
    collectStepErrors() {
      let errors = []
      let invalidCustomFieldIndexes = []
      let comp = this.currentStep.component
      if (comp === 'SelectService') {
        if (dig(this, 'inspection.services', []).length === 0)
          errors.push('service')
        let property = this.inspection.property
        if (!property) errors.push('property')
        if (property) {
          if (dig(property, 'attributes.address', '').length === 0)
            errors.push('address')
          if (dig(property, 'attributes.zip', '').length === 0)
            errors.push('zip')
          if (this.company.attributes.company_type.can_use_square_footage) {
            // Square feet validation
            const squareFeet = Number(
              dig(property, 'attributes.square_feet', '')
            )
            if (squareFeet === 0) {
              errors.push('square_feet')
            } else if (!Number.isInteger(squareFeet)) {
              errors.push('square_feet_numericality')
            } else if (squareFeet <= 0) {
              errors.push('square_feet_negative')
            }
          }
          if (this.company.attributes.company_type.can_use_year_built) {
            // Years built validation
            const yearsBuilt = Number(
              dig(property, 'attributes.year_built', '')
            )
            const currentYear = new Date().getFullYear()
            if (yearsBuilt === 0) {
              errors.push('year_built')
            } else if (!Number.isInteger(yearsBuilt)) {
              errors.push('year_built_numericality')
            } else if (yearsBuilt <= 0) {
              errors.push('year_built_negative')
            } else if (yearsBuilt > currentYear) {
              errors.push('year_built_in_the_future')
            }
          }
        }
        let customError = false
        this.inspection.custom_fields_attributes.forEach((cf, index) => {
          if (
            cf.attributes.required &&
            cf.attributes.response_format !== 'checkbox' &&
            dig(cf, 'attributes.value', '').length === 0
          ) {
            customError = true
            // Recorded alongside the unchanged `custom` code so the inline errors can mark the
            // specific offending fields while the toast keeps its single summary sentence.
            invalidCustomFieldIndexes.push(index)
          }
        })

        if (customError) errors.push('custom')
      } else if (comp === 'ContactInfo') {
        let buyerEmail = dig(this, 'inspection.buyer.attributes.email', '')
        let buyerShowAsCompany = dig(
          this,
          'inspection.buyer.attributes.show_as_company',
          false
        )
        let buyerFirstName = dig(
          this,
          'inspection.buyer.attributes.first_name',
          ''
        )
        let buyerLastName = dig(
          this,
          'inspection.buyer.attributes.last_name',
          ''
        )
        let buyerPhone = dig(this, 'inspection.buyer.attributes.phone', '')

        if (buyerEmail.length === 0) {
          errors.push('client_email')
        } else if (!buyerEmail.match(/^[^@\s]+@[^@\s]+$/)) {
          errors.push('client_email_invalid')
        }

        if (buyerFirstName.length === 0) {
          errors.push('client_first_name')
        }

        if (!buyerShowAsCompany && buyerLastName.length === 0) {
          errors.push('client_last_name')
        }

        // There shuld be at least 8 digits
        if (buyerPhone.replace(/[^0-9]/g, '').length <= 8) {
          errors.push('client_phone')
        }

        let buyer2Email = dig(this, 'inspection.buyer_2.attributes.email', '')
        if (buyer2Email.length > 0 && !buyer2Email.match(/^[^@\s]+@[^@\s]+$/))
          errors.push('client_2_email_invalid')

        let agentEmail = dig(this, 'inspection.agent.attributes.email', '')
        if (agentEmail.length > 0 && !agentEmail.match(/^[^@\s]+@[^@\s]+$/))
          errors.push('agent_email_invalid')

        let sellEmail = dig(
          this,
          'inspection.selling_agent.attributes.email',
          ''
        )
        if (sellEmail.length > 0 && !sellEmail.match(/^[^@\s]+@[^@\s]+$/))
          errors.push('selling_agent_email_invalid')
      } else if (comp === 'SelectDate') {
        if (dig(this, 'inspection.attributes.datetime', '').length === 0)
          errors.push('datetime')
        if (
          this.company.attributes.settings.scheduler_allow_inspector_choice &&
          !dig(this, 'inspection.inspector')
        )
          errors.push('inspector')
      }

      return { errors, invalidCustomFieldIndexes }
    },
    validateStep() {
      const { errors, invalidCustomFieldIndexes } = this.collectStepErrors()

      // Unchanged: the toast stays exactly as it was (SHUA-4319 dev note — keep it).
      this.displayErrors(errors)

      this.fieldErrors = this.fieldErrorsFor(
        errors,
        this.currentStep.component,
        invalidCustomFieldIndexes
      )

      return errors.length === 0
    },
    // `undefined` for a step that has no validation of its own — ReviewInspection does not
    // declare the prop, and Vue 2 would apply an undeclared object attribute to its root
    // element as `field-errors="[object Object]"`. `undefined` is dropped instead.
    fieldErrorsForStep(comp) {
      if (!FIELD_ERROR_CODES[comp]) return undefined

      return comp === this.currentComponent ? this.fieldErrors : {}
    },
    // Codes -> `{ fieldKey: message }`. The first code listed for a field wins, so the more
    // specific message ("must be a whole number") beats the generic one ("add the square
    // footage") when both are present.
    fieldErrorsFor(errors, comp, invalidCustomFieldIndexes = []) {
      const messages = {}
      const codes = FIELD_ERROR_CODES[comp] || []

      codes.forEach(([code, field, message]) => {
        if (errors.includes(code) && !messages[field]) messages[field] = message
      })

      if (comp === 'SelectService') {
        invalidCustomFieldIndexes.forEach((index) => {
          messages[`custom_${index}`] = 'Please complete this field'
        })
      }

      // The Company/Organization control binds the same `first_name`, so an empty one raises
      // `client_first_name` in both modes and the static table can't tell them apart. Only the
      // copy differs — naming the field the client can actually see (SHUA-4319 fix #4).
      if (
        comp === 'ContactInfo' &&
        messages.client_first_name &&
        this.buyerShowAsCompany
      ) {
        messages.client_first_name = COMPANY_NAME_INLINE_MESSAGE
      }

      return messages
    },
    // Only ever NARROWS what is already on screen. Recomputing from scratch would light up
    // fields the client hasn't reached yet the moment they fix an earlier one; intersecting
    // with the keys already displayed means a corrected field clears itself and nothing new
    // appears until the next NEXT tap.
    refreshFieldErrors() {
      const shown = Object.keys(this.fieldErrors)
      if (shown.length === 0) return

      const { errors, invalidCustomFieldIndexes } = this.collectStepErrors()
      const fresh = this.fieldErrorsFor(
        errors,
        this.currentStep.component,
        invalidCustomFieldIndexes
      )
      const next = {}

      shown.forEach((key) => {
        if (fresh[key]) next[key] = fresh[key]
      })

      this.fieldErrors = next
    },
    // Scroll the FIRST invalid control into view and focus it. This is the half of the fix that
    // works identically hosted and embedded: a scroll propagates to the parent frame, whereas
    // the `position: fixed` toast resolves against the iframe's viewport and lands off-screen.
    focusFirstInvalidField() {
      const el = this.firstInvalidElement()
      if (!el) return

      const margin = this.isFrame ? SCROLL_MARGIN_TOP_IFRAME : SCROLL_MARGIN_TOP
      const top = el.getBoundingClientRect().top + window.pageYOffset - margin

      // `scroll-margin-top` covers `scrollIntoView` on modern engines; scrolling to a computed
      // offset instead keeps the control clear of the fixed nav everywhere, and `block:'center'`
      // would over-scroll a control that is already near the top of a short step.
      window.scrollTo({ left: 0, top: Math.max(top, 0), behavior: 'smooth' })

      // Focusing is what tells a screen reader (and the client) where the problem is; `preventScroll`
      // so the browser's own focus scroll doesn't fight the smooth scroll above.
      const target = this.focusableWithin(el)
      if (target) target.focus({ preventScroll: true })
    },
    // Some targets are containers, not controls: the `datetime` error points at SelectDate's
    // root and a custom-field error at its `additional-info-field__<index>` wrapper. Calling
    // `focus()` on a plain <div> is a silent no-op, which would leave `document.activeElement`
    // on BODY — the exact symptom the audit measured. So prefer the first real control inside.
    focusableWithin(el) {
      if (this.isFocusable(el)) return el

      // Array.from rather than iterating the NodeList directly — this runs on old mobile
      // Safari, which is the whole point of the ticket.
      const candidates = Array.from(
        el.querySelectorAll(
          'input:not([type="hidden"]), select, textarea, button, [tabindex]'
        )
      )

      return candidates.find((candidate) => this.isFocusable(candidate)) || null
    },
    isFocusable(el) {
      if (!el || typeof el.focus !== 'function') return false
      if (el.disabled) return false

      // A container only counts once it opts in; `tabindex` is what the SelectDate wrapper
      // uses to become programmatically focusable.
      const tag = el.tagName
      if (['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'A'].includes(tag)) return true

      return el.hasAttribute('tabindex')
    },
    firstInvalidElement() {
      const comp = this.currentStep.component
      const order = (FIELD_ERROR_ORDER[comp] || []).concat(
        // Custom fields render after everything else on SelectService. Sorted numerically so
        // `custom_10` doesn't sort before `custom_2`.
        Object.keys(this.fieldErrors)
          .filter((key) => key.startsWith('custom_'))
          .sort((a, b) => Number(a.slice(7)) - Number(b.slice(7)))
      )

      for (const key of order) {
        if (!this.fieldErrors[key]) continue

        const selectors = key.startsWith('custom_')
          ? [`#additional-info-field__${key.slice(7)}`]
          : FIELD_ERROR_TARGETS[key] || []

        for (const selector of selectors) {
          const el = document.querySelector(selector)
          if (el) return el
        }
      }

      return null
    },
  },
}
</script>

<style scoped>
.page {
  padding-top: 2em;
}
.button-row {
  width: 100%;
  margin-top: 2em;
  text-align: right;
}

.schedule-stepper {
  overflow: hidden !important;
}

.scheduler-select {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.7);
  align-items: flex-start;
  align-content: center;
}

.scheduler-select-header {
  color: #fff;
  font-size: 1.25em;
  padding: 1rem;
  background-color: #3577A8;
  border-bottom: 5px solid #2B6088;
  font-size: 2em;
  font-weight: 300;
}

.scheduler-select-inner {
  background-color: #fff;
  box-shadow: 0 16px 28px 0 rgba(0, 0, 0, 0.22),
    0 25px 55px 0 rgba(0, 0, 0, 0.21);
}

.scheduler-select-body {
  padding: 2em;
}

.consent-prompt-message {
  line-height: 1.4;
  padding-bottom: 1.5em;
  text-align: center;
}

.consent-prompt-actions .consent-prompt-btn {
  display: inline-block;
  margin: 0 0.5em 0.5em;
}

.scheduler-block {
  padding: 2em 1em;
  background-color: #fff;
  text-align: center;
  transition: background 0.1s linear;
  cursor: pointer;
}

.scheduler-block:hover {
  background-color: #ffffff;
}

.scheduler-block .fa {
  font-size: 4em;
  color: #3577A8;
}

.scheduler-block .scheduler-name {
  font-size: 1.1em;
  margin: 0.5em;
}

.ineligible-wrap {
  position: absolute;
  top: 0;
  left: 0;
  text-align: center;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 1.5em;
}

@media (max-width: 800px) {
  .button-row {
    text-align: center;
  }
}
</style>

<style lang="scss">
body.scheduler {
  .flatpickr-weekdaycontainer {
    display: flex;
    flex: 1;
  }
}

#schedule {
  .v-stepper {
    overflow: visible;
    .v-stepper__items {
      overflow: visible;
      .v-stepper__wrapper {
        overflow: visible;
      }
    }
  }
  border-top: 1px solid #d9d9d9;

  .application--wrap {
    min-height: 55vh;
  }

  h3 {
    color: #2B6088;
    font-weight: 300;
    border: none;
  }

  .fill-width {
    width: 100%;
  }

  .v-stepper__label {
    text-shadow: none;
  }

  .v-input__slot:before {
    display: none;
  }
  .v-input__control .v-input__slot:after {
    display: none;
  }
  .v-menu {
    border-bottom: 1px solid #9e9e9e;
  }
  .v-select__selections input {
    border-bottom: none;
  }
  .v-select .v-input__slot .v-select__slot {
    border-bottom: 1px solid #9e9e9e;
  }

  .v-snack__content {
    height: auto;
  }

  // SHUA-4319 — keep a control scrolled to by `focusFirstInvalidField` clear of the 56px
  // MaterializeCSS fixed nav (`_nav_minimal.haml` -> `.navbar-fixed`). Belt-and-braces with the
  // computed offset in `focusFirstInvalidField`, for anything that scrolls the field into view
  // by other means (native focus, anchor jumps).
  .v-input,
  .custom-field,
  .calendar-ref {
    scroll-margin-top: 72px;
  }

  // The standalone messages for the surfaces with no Vuetify control to hang one on:
  // the FullCalendar date picker, flat-pickr custom fields, and the Places search box.
  .scheduler-field-error {
    font-size: 12px;
    line-height: 1.4;
    padding: 4px 0 0;
  }

  &.iframe {
    min-height: 100vh;
    background-color: #fff;

    // The embed renders no nav (`vue.html.haml` gates it on `params[:iframe] != "1"`), so the
    // hosted offset would over-scroll by a nav that isn't there.
    .v-input,
    .custom-field,
    .calendar-ref {
      scroll-margin-top: 16px;
    }

    .container {
      width: 100%;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      align-content: baseline;
      justify-content: center;
      background-color: #fff;
    }

    .v-stepper {
      width: inherit;
    }

    .button-row {
      text-align: center;
      .btn {
        margin: 0;
      }
    }
  }
}

// Disable android auto-zoom
input {
  touch-action: none;
}
</style>
